import { eventIsTracked } from '@/tracking/analytics/utils';
import { EventName } from '@/tracking/analytics/types';
import { pushDataLayer } from '@/tracking/analytics';
import { Money } from '@libs/money/money.interfaces';
import { AnyProduct } from '@shared/interfaces/product';
import {
    mapBaggageItem,
    mapCampaignDiscountItem,
    mapCartAsItems,
    mapGordianSeatingItem,
    mapProductsAsItems,
} from '@/tracking/analytics/Checkout/Helpers/map';
import { Cart, FlightProduct, SeatingProduct } from '@/api/Cart/cart.interfaces';
import { DiscountCodeResponse } from '@/api/Checkout/checkout.interfaces';
import { getSeatingTotalPriceForAllProducts } from '@/helpers/seating';
import { Baggage } from '@shared/interfaces/baggage';

export const logAnalyticsCustomerDetailsInitiated = (totalToPay: Money, margin: Money) => {
    if (!eventIsTracked(EventName.CHECKOUT_CUSTOMER_DETAILS_INITIATED)) {
        pushDataLayer({
            event: EventName.CHECKOUT_CUSTOMER_DETAILS_INITIATED,
            ecommerce: {
                currency: totalToPay.Currency,
                value: totalToPay.Amount,
                margin: margin?.Amount,
                items: [],
            },
        });
    }
};

export const logAnalyticsCustomerDetailsCompleted = (totalToPay: Money) => {
    if (!eventIsTracked(EventName.CHECKOUT_CUSTOMER_DETAILS_COMPLETED)) {
        pushDataLayer({
            event: EventName.CHECKOUT_CUSTOMER_DETAILS_COMPLETED,
            ecommerce: {
                currency: totalToPay.Currency,
                value: totalToPay.Amount,
                items: [],
            },
        });
    }
};

export const logAnalyticsCustomerEmailCompleted = (totalToPay: Money, margin: Money) => {
    if (!eventIsTracked(EventName.CHECKOUT_CUSTOMER_EMAIL_COMPLETED)) {
        pushDataLayer({
            event: EventName.CHECKOUT_CUSTOMER_EMAIL_COMPLETED,
            ecommerce: {
                currency: totalToPay.Currency,
                value: totalToPay.Amount,
                margin: margin?.Amount,
                items: [],
            },
        });
    }
};

export const logAnalyticsCheckoutInitiated = (
    cart: Cart,
    totalToPay: Money,
    isLoggedIn: boolean
) => {
    if (!eventIsTracked(EventName.CHECKOUT_PAGE_INITIATED)) {
        pushDataLayer({
            event: EventName.CHECKOUT_PAGE_INITIATED,
            ecommerce: {
                currency: totalToPay.Currency,
                value: totalToPay.Amount,
                items: mapCartAsItems(cart),
            },
            isLoggedIn,
        });
    }
};

export const logAnalyticsCartExpired = (currency: string) => {
    if (!eventIsTracked(EventName.CHECKOUT_CART_EXPIRED)) {
        pushDataLayer({
            event: EventName.CHECKOUT_CART_EXPIRED,
            ecommerce: {
                currency,
                value: 0,
                items: [],
            },
        });
    }
};

export const logAnalyticsAddCampaign = (discountResponse: DiscountCodeResponse) => {
    if (discountResponse?.Discount) {
        pushDataLayer({
            event: EventName.CHECKOUT_ADD_PRODUCT,
            ecommerce: {
                currency: discountResponse.Discount.Currency,
                value: 0,
                items: [mapCampaignDiscountItem(discountResponse.Discount)],
            },
        });
    }
};

export const logAnalyticsAddRemoveProduct = (
    product: AnyProduct,
    event: EventName.CHECKOUT_ADD_PRODUCT | EventName.CHECKOUT_REMOVE_PRODUCT
) => {
    pushDataLayer({
        event,
        ecommerce: {
            currency: product.Price.Currency,
            value: product.Price.Amount,
            items: mapProductsAsItems([product]),
        },
    });
};

const logAnalyticsAddRemoveGordianSeating = (
    seats: SeatingProduct[],
    flightProduct: FlightProduct,
    event: EventName.CHECKOUT_ADD_PRODUCT | EventName.CHECKOUT_REMOVE_PRODUCT
) => {
    const totalPrice = getSeatingTotalPriceForAllProducts(seats);
    pushDataLayer({
        event,
        ecommerce: {
            value: totalPrice?.Amount ?? 0,
            currency: totalPrice?.Currency ?? '',
            items: seats.map((seat) => mapGordianSeatingItem(seat, flightProduct)),
        },
    });
};

export const logAnalyticsGordianSeatingChanged = (
    flightProduct: FlightProduct,
    seats: SeatingProduct[],
    prevSeats: SeatingProduct[]
) => {
    const removedSeats = prevSeats.filter((seat) => !seats.some(({ Id }) => Id === seat.Id));
    const addedSeats = seats.filter((seat) => !prevSeats.some(({ Id }) => Id === seat.Id));
    if (removedSeats.length) {
        logAnalyticsAddRemoveGordianSeating(
            removedSeats,
            flightProduct,
            EventName.CHECKOUT_REMOVE_PRODUCT
        );
    }
    if (addedSeats.length) {
        logAnalyticsAddRemoveGordianSeating(
            addedSeats,
            flightProduct,
            EventName.CHECKOUT_ADD_PRODUCT
        );
    }
};

export const logAnalyticsAddRemoveBaggage = (
    baggage: Baggage,
    flightProduct: FlightProduct,
    event: EventName.CHECKOUT_ADD_PRODUCT | EventName.CHECKOUT_REMOVE_PRODUCT
) => {
    pushDataLayer({
        event,
        ecommerce: {
            value: baggage.Price.Amount,
            currency: baggage.Price.Currency,
            items: [mapBaggageItem(baggage, flightProduct)],
        },
    });
};
